import { baseApiService } from './base'
import type { WorkflowsResponse, PaginatedResponse } from './types'

export interface WorkflowType {
  [key: string]: string
}

export interface WorkflowPhase {
  phase: string
}

export interface WorkflowParams {
  pageSize: number
  pageStart: number
  sortBy: string
  sortDesc: boolean
  id?: number[]
  workflowTypes?: string[]
  workflowNumbers?: string[]
  currentPhases?: string[]
  workflowInitiators?: number[]
  assignedTo?: number[]
  startDateFrom?: string
  startDateTo?: string
  productIds?: number[]
  fsiIds?: number[]
  documentTypeIds?: number[]
  workflowStatus?: string
  [key: string]: string | number | boolean | string[] | number[] | undefined
}

export interface WorkflowProduct {
  id: number
  name: string
  propertyValue: string | null
}

export async function getWorkflows(): Promise<WorkflowsResponse> {
  return baseApiService.fetch<WorkflowsResponse>('/api/v2/workflows/dashboard', {
    params: { pageSize: 5, pageStart: 0 }
  })
}

export async function getWorkflowsFiltered(params: WorkflowParams): Promise<WorkflowsResponse> {
  return baseApiService.fetch<WorkflowsResponse>('/api/v2/workflows', {
    params
  })
}

export async function getWorkflowTypes(): Promise<WorkflowType> {
  return baseApiService.fetch<WorkflowType>('/api/workflows/types')
}

export async function getWorkflowPhases(): Promise<string[]> {
  return baseApiService.fetch<string[]>('/api/workflows/phases')
}

export async function getWorkflowProducts(): Promise<WorkflowProduct[]> {
  return baseApiService.fetch<WorkflowProduct[]>('/api/etmf/overview/dropdown/includeproperty/PRODUCT')
}

export async function getWorkflowNumbers(params: {
  pageSize?: number
  pageStart?: number
  sortBy?: string
  sortDesc?: boolean
  id?: string
}): Promise<PaginatedResponse<{ id: number; formattedId: string }>> {
  return baseApiService.fetch<PaginatedResponse<{ id: number; formattedId: string }>>('/api/v2/workflows', {
    params: {
      pageSize: params.pageSize ?? 70,
      pageStart: params.pageStart ?? 0,
      sortBy: params.sortBy ?? 'ID',
      sortDesc: params.sortDesc ?? false,
      ...(params.id && { id: params.id })
    }
  })
}

// For InfiniteScrollSelect async picker
export async function getOptionsForAsyncPicker(
  search: string,
  pageStart: number
): Promise<{ options: Array<{ value: number; label: string }>; hasMore: boolean }> {
  // Extract numeric ID from search string
  const id = search.replace(/\D/g, '')
  const pageSize = 70

  const params: {
    pageStart: number
    pageSize: number
    sortBy: string
    sortDesc: boolean
    id?: string
  } = {
    pageStart,
    pageSize,
    sortBy: 'ID',
    sortDesc: false
  }

  // If there's a numeric ID, use the picker endpoint, otherwise use regular endpoint
  const endpoint = id ? '/api/v2/workflows/picker' : '/api/v2/workflows'
  if (id) {
    params.id = id
  }

  const response = await baseApiService.fetch<PaginatedResponse<{ id: number; formattedId: string }>>(endpoint, {
    params
  })

  const hasMore = response.elements.length > 0 && (pageStart + 1) * pageSize < response.totalElements

  return {
    options: response.elements
      .map((item) => ({
        value: item.id,
        label: item.formattedId
      }))
      .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase())),
    hasMore
  }
}

export interface FolderNode {
  id: number
  fileName: string
  type: 'FOLDER' | 'FILE'
  systemFolderType?: string
  inWorkflow: boolean
  aclLevel: string
  transferInProgress: boolean
  studyBlockedForSelection: boolean
  children?: FolderNode[]
  hasChildren?: boolean
  isExpanded?: boolean
}

export async function getFolderNode(folderId: number): Promise<FolderNode> {
  return baseApiService.fetch<FolderNode>(`/api/files/${folderId}/filepickersinglenode`, {
    params: {
      obsolete: false,
      onlyFolders: true,
      sortBy: 'name',
      sortDesc: false
    }
  })
}

export async function getFolderChildren(folderId: number): Promise<FolderNode[]> {
  return baseApiService.fetch<FolderNode[]>(`/api/files/${folderId}/filepickerlist`, {
    params: {
      obsolete: false,
      onlyFolders: true,
      sortBy: 'name',
      sortDesc: false
    }
  })
}

export interface WorkflowAction {
  id: string
  label: string
  enabled: boolean
}

export interface WorkflowActionsResponse {
  canViewComments: boolean
  canCancel: boolean
  canSuspend: boolean
  canResume: boolean
  canUpdateEffectiveDate: boolean
  canEditExpirationDate: boolean
  multifileWorkflow: boolean
}

export interface DetailedWorkflow {
  id: number
  workflowNo: string
  initialApprovers: unknown[]
  description: string
  workflowType: string
  readableType: string
  initiatorUserId: number
  initiatorName: string
  collaborators: unknown[]
  parentFolderId: number
  startDate: number
  status: string
  draftFolderId: number
  workflowSteps: WorkflowStep[]
  filename: string
  canEditWorkflow: boolean
  canEditWorkflowCollaborators: boolean
  canEditWorkflowReviewers: boolean
  canEditWorkflowApprovers: boolean
  canEditRejectionReviewers: boolean
  canEditWorkflowTrainees: boolean
  canAddUserToNOfAllTaskGroup: boolean
  flags: WorkflowActionsResponse
  isInUserRejectMode: boolean
  requireJustification: boolean
  customJustification: boolean
  files: WorkflowDetailFile[]
  justificationText?: string
  expirationDate?: number
  requireExpirationDate?: boolean
}

export interface WorkflowStep {
  id?: number
  type: string
  readableType: string
  status: string
  createdAt?: number
  taskTeamSteps: TaskTeamStep[]
  effectiveDateTaskId?: number
  effectiveDate?: number
}

export interface TaskTeamStep {
  id?: number
  processingType: string
  taskTeamItems: TaskTeamItem[]
  nofAllValue: number
  rejectionReview?: boolean
  taskTeamStepLabel?: { text: string }
  // Computed properties added during workflow assembly
  insufficientNOOfApprovers?: boolean
  activeStep?: boolean
  countOfDone?: number
}

export interface TaskTeamItem {
  userId: number
  userName: string
  userEmail: string
  startDate?: number
  endDate?: number
  status: string
}

export interface WorkflowDetailFile {
  sourceFsi?: FileSystemItem
  draftFsi?: FileSystemItem
  targetFsi?: FileSystemItem
}

export interface FileSystemItem {
  id: number
  name: string
  parentId: number
  targetParentFolderName: string
  aclLevel: string
  type: string
  isConvertable: boolean
  viewed: boolean
  mustBeSeen: boolean
  isBlindedDocument: boolean
  expired: boolean
  versionId?: number
  minorVersionId?: number
  // Additional metadata fields
  title?: string
  createdAt?: number
  expirationDate?: number
  documentDate?: number
  status?: string
  statusOutdated?: boolean
}

export async function getWorkflow(workflowId: number): Promise<DetailedWorkflow> {
  return baseApiService.fetch<DetailedWorkflow>(`/api/workflows/${workflowId}`)
}

export interface WorkflowComment {
  id: number
  userId: number
  userFullName: string
  userEmail: string
  date: number
  workflowStepType: string
  workflowStepTypeDescription: string
  comment?: string
  rejectReasonText?: string
  isCustomRejectionReason?: boolean
}

export interface WorkflowCommentsResponse {
  files: WorkflowDetailFile[]
  comments: WorkflowComment[]
}

export async function getWorkflowComments(workflowId: number): Promise<WorkflowCommentsResponse> {
  return baseApiService.fetch<WorkflowCommentsResponse>(`/api/workflows/${workflowId}/reviewercomments`)
}

export async function getWorkflowActions(workflowId: number): Promise<WorkflowActionsResponse> {
  const response = await baseApiService.fetch<{ flags: WorkflowActionsResponse }>(`/api/workflows/${workflowId}`)
  return response.flags
}

export interface ExportFilter {
  status?: string | null
  createdById?: string | null
  endDate?: string | null
  startDate?: string | null
  fsiId?: string | null
  endDateSpec?: string
  startDateSpec?: string
  filterId?: string
}

export interface ExportMeta {
  sortBy?: string
  sortDesc?: string | boolean
  pageSize?: number
  pageStart?: number
}

export async function exportCRsReports(filter: ExportFilter, meta: ExportMeta): Promise<void> {
  const requestObj = { filter, meta }
  return baseApiService.fetch('/api/reports/changerequestexport', {
    method: 'POST',
    body: requestObj
  })
}

export async function exportRejectionsReports(filter: any, meta: ExportMeta): Promise<void> {
  const requestObj = { filter, meta }
  return baseApiService.fetch('/api/reports/rejectexport', {
    method: 'POST',
    body: requestObj
  })
}

export async function exportWorkflowsReports(filter: any, meta: ExportMeta): Promise<void> {
  const requestObj = { filter, meta }
  return baseApiService.fetch('/api/reports/readandacknowledgeexport', {
    method: 'POST',
    body: requestObj
  })
}
export interface WorkflowFilesFilter {
  sortBy?: string
  sortDesc?: boolean
  [key: string]: string | number | boolean | undefined
}

export interface WorkflowFilesResponse {
  elements: WorkflowDetailFile[]
  totalElements: number
  latestDocModifiedTs?: number
}

/**
 * Get workflow files with filtering and sorting
 * @param workflowId - Workflow ID
 * @param filter - Filter parameters (sortBy, sortDesc, etc.)
 * @param showAllDocs - If true, shows all docs; if false, shows only not reviewed docs
 * @param sortBy - Sort field (default: 'name')
 * @param sortDesc - Sort descending (default: false)
 */
export async function getWorkflowFiles(
  workflowId: number,
  filter?: WorkflowFilesFilter,
  showAllDocs = true,
  sortBy = 'name',
  sortDesc = false
): Promise<WorkflowFilesResponse> {
  const queryParam = showAllDocs ? 'showAllDocs=true' : 'showNotReviewedDocs=true'

  const params = {
    ...filter,
    sortBy,
    sortDesc
  }

  return baseApiService.fetch<WorkflowFilesResponse>(`/api/v2/workflows/${workflowId}/getworkflowfiles?${queryParam}`, {
    params
  })
}

/**
 * Validate workflow files
 * @param workflowId - Workflow ID
 */
export async function validateWorkflowFiles(workflowId: number): Promise<{ value: boolean; message?: string }> {
  return baseApiService.fetch<{ value: boolean; message?: string }>(`/api/v2/workflows/${workflowId}/validate`)
}

export interface CancelWorkflowsRequest {
  workflowIds: number[]
  comment: string
  esignature?: {
    username: string
    password: string
    reason: string
  }
}

/**
 * Cancel workflows with comment and optional e-signature
 * @param workflowIds - Array of workflow IDs to cancel
 * @param comment - Cancellation comment
 * @param esignature - Optional e-signature validation
 */
export async function cancelWorkflowsWithComments(
  workflowIds: number[],
  comment: string,
  esignature?: { username: string; password: string; reason: string }
): Promise<void> {
  const requestBody: CancelWorkflowsRequest = {
    workflowIds,
    comment,
    ...(esignature && { esignature })
  }

  return baseApiService.fetch<void>('/api/workflows/cancelWorkflows', {
    method: 'PATCH',
    body: JSON.stringify(requestBody)
  })
}

export interface UpdateWorkflowRequest {
  description: string
  justificationTextId?: number | null
  customJustificationText?: string | null
  expirationDate?: number
  [key: string]: string | number | null | undefined
}

export interface Justification {
  id: number
  text: string
}

export interface JustificationsResponse {
  elements: Justification[]
}

export async function updateWorkflow(workflowId: number, data: UpdateWorkflowRequest): Promise<void> {
  return baseApiService.fetch<void>(`/api/workflows/${workflowId}/update`, {
    method: 'PATCH',
    body: data
  })
}

export async function getJustifications(): Promise<JustificationsResponse> {
  return baseApiService.fetch<JustificationsResponse>('/api/justifications', {
    params: { type: 'JUSTIFICATION_CHANGE' }
  })
}

/**
 * Get workflow approvers data
 * @param workflowId - The workflow ID
 * @param transformForReact - Whether to transform data for React components
 */
export async function getCRApprovers(workflowId: number, transformForReact: boolean = false): Promise<any> {
  return baseApiService.fetch<any>(`/api/workflows/${workflowId}/approvers`)
}

/**
 * Update workflow approvers
 * @param workflowId - The workflow ID
 * @param data - Approvers data
 * @param transformedData - Whether data is already transformed
 */
export async function updateCRApprovers(
  workflowId: number,
  data: any,
  transformedData: boolean = false
): Promise<void> {
  return baseApiService.fetch<void>(`/api/workflows/${workflowId}/approvers`, {
    method: 'PUT',
    body: data
  })
}

/**
 * Get workflow reviewers
 * @param workflowId - The workflow ID
 * @param transformForReact - Whether to transform data for React components
 */
export async function getCRReviewers(workflowId: number, transformForReact: boolean = false): Promise<any> {
  return baseApiService.fetch<any>(`/api/workflows/${workflowId}/reviewers`)
}

/**
 * Update workflow reviewers
 * @param workflowId - The workflow ID
 * @param data - Reviewers data
 * @param transformedData - Whether data is already transformed
 */
export async function updateCRReviewers(
  workflowId: number,
  data: any,
  transformedData: boolean = false
): Promise<void> {
  return baseApiService.fetch<void>(`/api/workflows/${workflowId}/reviewers`, {
    method: 'PUT',
    body: data
  })
}

/**
 * Get workflow trainees
 * @param workflowId - The workflow ID
 * @param transformForReact - Whether to transform data for React components
 */
export async function getCRTrainees(workflowId: number, transformForReact: boolean = false): Promise<any> {
  return baseApiService.fetch<any>(`/api/workflows/${workflowId}/trainees`)
}

/**
 * Update workflow trainees
 * @param workflowId - The workflow ID
 * @param data - Trainees data
 * @param transformedData - Whether data is already transformed
 */
export async function updateCRTrainees(workflowId: number, data: any, transformedData: boolean = false): Promise<void> {
  return baseApiService.fetch<void>(`/api/workflows/${workflowId}/trainees`, {
    method: 'PUT',
    body: data
  })
}

/**
 * Get workflow rejection reviewers
 * @param workflowId - The workflow ID
 * @param transformForReact - Whether to transform data for React components
 */
export async function getCRRejectionReviewers(workflowId: number, transformForReact: boolean = false): Promise<any> {
  return baseApiService.fetch<any>(`/api/workflows/${workflowId}/rejectionreviewers`)
}

/**
 * Update workflow rejection reviewers
 * @param workflowId - The workflow ID
 * @param data - Rejection reviewers data
 * @param transformedData - Whether data is already transformed
 */
export async function updateCRRejectionReviewers(
  workflowId: number,
  data: any,
  transformedData: boolean = false
): Promise<void> {
  return baseApiService.fetch<void>(`/api/workflows/${workflowId}/rejectionreviewers`, {
    method: 'PUT',
    body: data
  })
}

/**
 * Get workflow editors/collaborators
 * @param workflowId - The workflow ID
 * @param transformForReact - Whether to transform data for React components
 */
export async function getCREditors(workflowId: number, transformForReact: boolean = false): Promise<any> {
  return baseApiService.fetch<any>(`/api/workflows/${workflowId}/collaborators`)
}

/**
 * Update workflow editors/collaborators
 * @param workflowId - The workflow ID
 * @param data - Collaborators data containing { collaborators: steps[] }
 * @param transformedData - Whether data is already transformed
 */
export async function updateCREditors(workflowId: number, data: any, transformedData: boolean = false): Promise<void> {
  return baseApiService.fetch<void>(`/api/workflows/${workflowId}/collaborators`, {
    method: 'PUT',
    body: data
  })
}

/**
 * Get workflow by ID (alias for getWorkflow for AngularJS compatibility)
 */
export async function getById(workflowId: number): Promise<DetailedWorkflow> {
  return getWorkflow(workflowId)
}

/**
 * Add collaborators to a workflow's current phase
 * @param workflowId - The workflow ID
 * @param collaborators - Array of collaborator objects with id and systemHelperType
 */
export async function addCollaborators(
  workflowId: number,
  collaborators: Array<{ id: string; systemHelperType: string }>
): Promise<void> {
  const requestBody = collaborators
    .map((col) => {
      if (col.systemHelperType === 'USER') {
        return { user: { id: col.id } }
      } else if (col.systemHelperType === 'GROUP') {
        return { group: { id: col.id } }
      }
      return null
    })
    .filter((item): item is { user: { id: string } } | { group: { id: string } } => item !== null)

  return baseApiService.fetch<void>(`/api/workflows/${workflowId}/addnofallcollaborators`, {
    method: 'POST',
    body: requestBody as any
  })
}

/**
 * Request a document change (legacy requestDocumentChange)
 * @param request - Request body (any shape)
 * @returns Promise<any> (raw API response)
 * Logic matches legacy AngularJS: Api.one(baseURL).customPOST(request)
 */
export async function requestDocumentChange(request: any): Promise<any> {
  return baseApiService.fetch('/api/workflows', {
    method: 'POST',
    body: request
  })
}

// Export workflows API object for AngularJS compatibility
export const workflows = {
  requestDocumentChange: requestDocumentChange,
  getList: getWorkflowsFiltered,
  getWorkflowsForDashboard: getWorkflows,
  getWorkflows,
  getWorkflowsFiltered,
  getWorkflowTypes,
  getWorkflowPhases,
  getWorkflowProducts,
  getWorkflowNumbers,
  getFolderNode,
  getFolderChildren,
  getWorkflow,
  getById,
  getWorkflowComments,
  getWorkflowActions,
  exportCRsReports,
  exportRejectionsReports,
  exportWorkflowsReports,
  getWorkflowFiles,
  validateWorkflowFiles,
  cancelWorkflowsWithComments,
  updateWorkflow,
  getJustifications,
  getCRApprovers,
  updateCRApprovers,
  getCRReviewers,
  updateCRReviewers,
  getCRTrainees,
  updateCRTrainees,
  getCRRejectionReviewers,
  updateCRRejectionReviewers,
  getCREditors,
  updateCREditors,
  addCollaborators
}
